import { useMemo, useState, type FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { normalizarBusqueda } from '../../lib/texto';
import { descargarCSV } from '../../lib/exportCsv';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { TIPOS_MOVIMIENTO_MANUAL } from '../../types/domain';
import type { MovimientoConProducto } from '../../types/domain';
import type { ProductoRow, TipoMovimientoInventario } from '../../types/database.types';

/**
 * Módulo de Inventario (DAF v2, sección 16). Todo movimiento pasa por el
 * RPC `registrar_movimiento_inventario` (ver
 * supabase/schema_ventas_inventario.sql) para que el stock del producto
 * se actualice en la misma transacción que el movimiento — nunca se
 * escribe `productos.stock_actual` directamente desde el frontend.
 */
export function InventarioMovimientos() {
  const { perfil } = useAuth();
  const [mostrarForm, setMostrarForm] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  const { data: productos, refetch: refetchProductos } = useSupabaseQuery<ProductoRow[]>(async () =>
    supabase.from('productos').select('*').eq('estado', 'activo').order('nombre'),
  );

  // Lista liviana con TODOS los productos (activos e inactivos, sin
  // columnas de más) solo para resolver el buscador por nombre contra el
  // historial completo -- un movimiento viejo puede referenciar un
  // producto que hoy ya está inactivo, y la búsqueda debe encontrarlo
  // igual. Independiente de `productos` de arriba, que sí se filtra a
  // solo activos porque ese es el que alimenta el selector del formulario
  // (no tiene sentido registrar un movimiento nuevo contra un producto
  // inactivo).
  const { data: productosTodos } = useSupabaseQuery<Pick<ProductoRow, 'id' | 'nombre'>[]>(async () =>
    supabase.from('productos').select('id, nombre'),
  );

  const {
    data: movimientos,
    loading,
    error,
    refetch: refetchMovimientos,
  } = useSupabaseQuery<MovimientoConProducto[]>(async () => {
    let consulta = supabase
      .from('inventario_movimientos')
      .select('*, productos ( nombre )')
      .order('fecha', { ascending: false });

    const q = normalizarBusqueda(busqueda.trim());
    if (q) {
      // Se resuelve el nombre contra `productosTodos` (normalizando
      // tildes/mayúsculas, algo que un `ilike` de Postgres no hace por sí
      // solo) y se filtra el historial completo por esos ids -- así una
      // búsqueda encuentra movimientos viejos, no solo los últimos 50 que
      // se muestran sin filtro. Un límite más alto (500) porque, a
      // diferencia de la vista sin filtrar, aquí ya se acotó a un
      // producto puntual.
      const idsCoincidentes = (productosTodos ?? [])
        .filter((p) => normalizarBusqueda(p.nombre).includes(q))
        .map((p) => p.id);
      if (idsCoincidentes.length === 0) {
        return { data: [], error: null };
      }
      consulta = consulta.in('producto_id', idsCoincidentes).limit(500);
    } else {
      consulta = consulta.limit(50);
    }

    const { data, error } = await consulta;

    if (error) return { data: null, error };

    const items = (data as unknown as Array<Record<string, unknown>>).map((row) => ({
      ...(row as unknown as MovimientoConProducto),
      producto_nombre: (row.productos as { nombre: string } | null)?.nombre ?? '—',
    }));

    return { data: items, error: null };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busqueda, productosTodos]);

  const productosBajoStock = (productos ?? []).filter((p) => p.stock_actual <= p.stock_minimo);

  function alGuardar() {
    setMostrarForm(false);
    refetchProductos();
    refetchMovimientos();
  }

  const [descargando, setDescargando] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState<string | null>(null);

  /**
   * Descarga en un archivo compatible con Excel (CSV con BOM UTF-8, mismo
   * mecanismo ya usado en Reportes.tsx — sin depender de ninguna librería
   * nueva) el historial COMPLETO de movimientos del salón -- pedido
   * explícito del usuario ("quiero que cuando lo descargue en excel me
   * descargue todo"), sin importar la búsqueda activa ni el tope de
   * 50/500 filas que sí aplica a lo que se muestra en pantalla. Por eso
   * hace su propia consulta en vez de reusar `movimientos`: PostgREST
   * limita cada página a 1000 filas por defecto en Supabase, así que se
   * pagina con `.range()` hasta que una página vuelva con menos filas de
   * las pedidas -- señal de que ya no queda nada más por traer.
   */
  async function descargarExcel() {
    setDescargando(true);
    setErrorDescarga(null);
    try {
      const TAMANO_PAGINA = 1000;
      let desde = 0;
      let todos: MovimientoConProducto[] = [];
      for (;;) {
        const { data, error } = await supabase
          .from('inventario_movimientos')
          .select('*, productos ( nombre )')
          .order('fecha', { ascending: false })
          .range(desde, desde + TAMANO_PAGINA - 1);

        if (error) throw error;

        const pagina = (data as unknown as Array<Record<string, unknown>>).map((row) => ({
          ...(row as unknown as MovimientoConProducto),
          producto_nombre: (row.productos as { nombre: string } | null)?.nombre ?? '—',
        }));
        todos = todos.concat(pagina);

        if (pagina.length < TAMANO_PAGINA) break;
        desde += TAMANO_PAGINA;
      }

      const filas = todos.map((m) => [
        new Date(m.fecha).toLocaleString('es-CO'),
        m.producto_nombre,
        TIPOS_MOVIMIENTO_MANUAL.find((t) => t.value === m.tipo)?.label ?? m.tipo,
        m.cantidad,
        m.motivo ?? '',
        m.observacion ?? '',
      ]);
      descargarCSV('Inventario - historial completo', ['Fecha', 'Producto', 'Tipo', 'Cantidad', 'Motivo', 'Observación'], filas);
    } catch (err) {
      setErrorDescarga(err instanceof Error ? err.message : 'No se pudo descargar el historial completo.');
    } finally {
      setDescargando(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Inventario"
        description="Movimientos de entrada, salida y ajuste — DAF v2, sección 16"
        actions={
          <div className="flex gap-3">
            {movimientos && movimientos.length > 0 && (
              <button type="button" disabled={descargando} className="btn-secondary" onClick={descargarExcel}>
                {descargando ? 'Descargando…' : 'Descargar Excel'}
              </button>
            )}
            <button className="btn-primary" onClick={() => setMostrarForm((v) => !v)}>
              {mostrarForm ? 'Cancelar' : 'Registrar movimiento'}
            </button>
          </div>
        }
      />

      {errorDescarga && <p className="mb-4 text-sm text-red-600">{errorDescarga}</p>}

      {productosBajoStock.length > 0 && (
        <div className="card mb-4 border-amber-300 bg-amber-50">
          <p className="text-sm font-medium text-amber-900">
            {productosBajoStock.length} producto(s) por debajo del stock mínimo
          </p>
          <ul className="mt-1 text-sm text-amber-800">
            {productosBajoStock.slice(0, 6).map((p) => (
              <li key={p.id}>
                {p.nombre} — quedan {p.stock_actual} (mínimo {p.stock_minimo})
              </li>
            ))}
          </ul>
        </div>
      )}

      {mostrarForm && perfil && (
        <RegistrarMovimientoForm
          salonId={perfil.salon_id}
          productos={productos ?? []}
          onGuardado={alGuardar}
          onCancelar={() => setMostrarForm(false)}
        />
      )}

      <div className="mb-4">
        <input
          type="text"
          className="input-field max-w-sm"
          placeholder="Buscar por nombre de producto…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
      </div>

      {loading && <p className="text-sm text-onyx/50">Cargando…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {movimientos && movimientos.length === 0 && (
        <EmptyState
          title={busqueda.trim() ? 'Sin resultados' : 'Sin movimientos registrados todavía'}
          description={busqueda.trim() ? `Ningún producto coincide con "${busqueda}".` : undefined}
        />
      )}

      {movimientos && movimientos.length > 0 && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-onyx/10 text-left text-onyx/50">
                <th className="px-4 py-3 font-medium">Fecha</th>
                <th className="px-4 py-3 font-medium">Producto</th>
                <th className="px-4 py-3 font-medium">Tipo</th>
                <th className="px-4 py-3 font-medium">Cantidad</th>
                <th className="px-4 py-3 font-medium">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {movimientos.map((m) => (
                <tr key={m.id} className="border-b border-onyx/5 last:border-0">
                  <td className="px-4 py-3 text-onyx/60">
                    {new Date(m.fecha).toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })}
                  </td>
                  <td className="px-4 py-3 font-medium text-onyx">{m.producto_nombre}</td>
                  <td className="px-4 py-3 text-onyx/70">
                    {TIPOS_MOVIMIENTO_MANUAL.find((t) => t.value === m.tipo)?.label ?? m.tipo}
                  </td>
                  <td className={`px-4 py-3 font-medium ${m.cantidad > 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {m.cantidad > 0 ? '+' : ''}
                    {m.cantidad}
                  </td>
                  <td className="px-4 py-3 text-onyx/60">{m.motivo ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface RegistrarMovimientoFormProps {
  salonId: string;
  productos: ProductoRow[];
  onGuardado: () => void;
  onCancelar: () => void;
}

function RegistrarMovimientoForm({ salonId, productos, onGuardado, onCancelar }: RegistrarMovimientoFormProps) {
  const [productoId, setProductoId] = useState('');
  const [filtroProducto, setFiltroProducto] = useState('');
  const [tipo, setTipo] = useState<TipoMovimientoInventario>('entrada_compra');
  const [cantidad, setCantidad] = useState('1');
  const [motivo, setMotivo] = useState('');
  const [observacion, setObservacion] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // El filtro solo acota qué <option> se muestran -- el envío del
  // formulario sigue siendo por id vía el <select> normal, sin tocar
  // `registrar_movimiento_inventario`. Con salones que manejen catálogos
  // grandes, escribir es más rápido que desplazarse por el desplegable.
  const productosFiltrados = useMemo(() => {
    const q = normalizarBusqueda(filtroProducto.trim());
    if (!q) return productos;
    return productos.filter((p) => normalizarBusqueda(p.nombre).includes(q));
  }, [productos, filtroProducto]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const cantidadNum = Number(cantidad);
    if (!productoId || !cantidadNum || cantidadNum <= 0) {
      setError('Selecciona un producto y una cantidad mayor que cero');
      return;
    }

    setGuardando(true);
    const { error: err } = await supabase.rpc('registrar_movimiento_inventario', {
      p_salon_id: salonId,
      p_producto_id: productoId,
      p_tipo: tipo,
      p_cantidad: cantidadNum,
      p_motivo: motivo || null,
      p_observacion: observacion || null,
      p_referencia: null,
    });
    setGuardando(false);

    if (err) {
      setError(err.message);
      return;
    }

    onGuardado();
  }

  return (
    <form onSubmit={handleSubmit} className="card mb-4 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Producto *</span>
          <input
            type="text"
            className="input-field mb-2"
            placeholder="Escribe para buscar…"
            value={filtroProducto}
            onChange={(e) => setFiltroProducto(e.target.value)}
          />
          <select required className="input-field" value={productoId} onChange={(e) => setProductoId(e.target.value)}>
            <option value="" disabled>
              Selecciona un producto
            </option>
            {/* Si el producto ya elegido queda fuera del filtro (se siguió
                escribiendo después de seleccionar), se conserva como opción
                aparte para que el <select> no pierda el valor ya elegido. */}
            {productoId && !productosFiltrados.some((p) => p.id === productoId) && (
              (() => {
                const seleccionado = productos.find((p) => p.id === productoId);
                return seleccionado ? (
                  <option key={seleccionado.id} value={seleccionado.id}>
                    {seleccionado.nombre} (stock: {seleccionado.stock_actual})
                  </option>
                ) : null;
              })()
            )}
            {productosFiltrados.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre} (stock: {p.stock_actual})
              </option>
            ))}
          </select>
          {filtroProducto.trim() && productosFiltrados.length === 0 && (
            <p className="mt-1 text-sm text-onyx/50">Ningún producto coincide con la búsqueda.</p>
          )}
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Tipo de movimiento *</span>
          <select
            required
            className="input-field"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoMovimientoInventario)}
          >
            {TIPOS_MOVIMIENTO_MANUAL.map((t) => (
              <option key={t.value} value={t.value}>
                {t.entrada ? '+ ' : '− '}
                {t.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Cantidad *</span>
          <input
            required
            type="number"
            min={1}
            step="0.01"
            className="input-field"
            value={cantidad}
            onChange={(e) => setCantidad(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Motivo</span>
          <input className="input-field" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </label>
      </div>

      <label className="block">
        <span className="mb-1 block text-sm font-medium text-onyx/80">Observación</span>
        <textarea className="input-field" rows={2} value={observacion} onChange={(e) => setObservacion(e.target.value)} />
      </label>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-3">
        <button type="submit" disabled={guardando} className="btn-primary">
          {guardando ? 'Guardando…' : 'Registrar movimiento'}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
